import React, { ReactNode } from 'react';
import { IconName } from '@blueprintjs/icons';
import Box, { BoxProps } from 'ui-box';
import {
  minorScale,
  defaultFills,
  majorScale,
  Heading,
  Icon,
  Spinner,
} from '../../npaper';
import { ITask, Unit } from '../../stores';
import { SaleThumbnail } from '../sales';
import { UserThumbnail } from '../settings/UserThumbnail';
import { observer } from 'mobx-react-lite';
import { FormattedDate } from 'react-intl';
import moment from 'moment';
import { FormattedUnit } from '../../npaper/FormattedUnit';
import { TaskLogTracking } from './TaskLogTracking';

export const TaskCard: React.FC<{
  task: ITask;
  selected: boolean;
  isDragging?: boolean;
  onClick: () => void;
}> = observer(({ task, selected, isDragging, onClick }) => {
  return (
    <Box
      borderRadius={minorScale(1)}
      backgroundColor={defaultFills.white}
      padding={majorScale(2)}
      onClick={() => onClick()}
      boxShadow={
        selected && !isDragging
          ? `0 0 0 2px ${defaultFills.info[500]}`
          : undefined
      }
      transition={'box-shadow 150ms'}
      className={isDragging ? 'shadow-md' : undefined}
    >
      <TaskCardHeader task={task} />
      {task.description && (
        <Box is="p" marginTop={minorScale(0.5)}>
          {task.description}
        </Box>
      )}
      <Box display="flex" flexWrap="wrap" marginTop={minorScale(0.5)}>
        {task.due_date && (
          <TaskIconTag
            icon="flag"
            text={<FormattedDate format="short" value={task.due_date} />}
            color={task.overdue ? defaultFills.danger[500] : undefined}
          />
        )}

        {task.contacts.length > 0 && (
          <TaskIconTag icon="people" text={`${task.contacts.length}`} />
        )}

        {task.timeSpent && (
          <TaskIconTag
            icon="stopwatch"
            text={moment
              .duration(task.timeSpent, 'milliseconds')
              .format('hh:mm', { trim: false })}
            color={task.overBudget ? defaultFills.danger[500] : undefined}
          />
        )}

        {task.budget && (
          <TaskIconTag
            icon="take-action"
            text={<FormattedUnit unit={Unit.HOUR} value={task.budget} />}
            color={
              task.overBudget
                ? defaultFills.danger[500]
                : task.budgetInDanger
                ? defaultFills.warning[500]
                : undefined
            }
          />
        )}
      </Box>
      <Box
        marginTop={minorScale(3)}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        {task.sale ? (
          <SaleThumbnail sale={task.sale} />
        ) : task.sale === null ? (
          <Box display="flex" alignItems="center">
            <Icon
              icon="warning-sign"
              marginRight={minorScale(1)}
              color={defaultFills.warning[500]}
            />
            <p className="muted">Ikke faktureret</p>
          </Box>
        ) : (
          <Spinner size={16} />
        )}
        <Box>
          {task.responsible &&
            task.responsible.map((member, i) =>
              member ? (
                <UserThumbnail
                  key={member.id}
                  user={member.user}
                  marginLeft={minorScale(-1)}
                />
              ) : (
                <Spinner key={'index' + i} size={16} />
              )
            )}
        </Box>
      </Box>
    </Box>
  );
});

export const TaskCardHeader = observer(
  ({
    task,
    showTracking,
    ...props
  }: { task: ITask; showTracking?: boolean } & BoxProps<'div'>) => (
    <Box {...props} display="flex" alignItems="center">
      <Box>
        <Heading size={300}>{task.title}</Heading>
        <Box className="p small muted" marginTop={minorScale(0.5)}>
          {task.customer !== null ? (
            task.customer ? (
              `For ${task.customer.name}`
            ) : (
              <Spinner size={16} />
            )
          ) : (
            'Intet salg tilknyttet'
          )}
        </Box>
      </Box>
      {showTracking && (
        <Box marginLeft="auto">
          <TaskLogTracking task={task} />
        </Box>
      )}
    </Box>
  )
);

export const TaskIconTag: React.FC<
  {
    icon: IconName;
    text: ReactNode;
    color?: string;
  } & BoxProps<'div'>
> = ({ icon, text, color = defaultFills.muted, ...props }) => (
  <Box
    display="flex"
    alignItems="center"
    marginRight={minorScale(3)}
    marginTop={majorScale(1)}
    {...props}
  >
    <Icon icon={icon} marginRight={minorScale(1)} color={color} />
    <span className="p small" style={{ color }}>
      {text}
    </span>
  </Box>
);
